import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { DocumentDownloadService } from 'src/app/services/download-file/download-file.service';

/**
 * Indicateur flottant de téléchargements — pilule en bas à droite de l'écran,
 * alimentée par `DocumentDownloadService.downloads` (montée une seule fois dans
 * AppComponent, visible depuis toutes les pages).
 *
 * Trois états par fichier : spinner (en cours), ✓ vert (terminé), ✕ rouge (échec).
 */
@Component({
  selector: 'app-download-indicator',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="dl-stack" *ngIf="downloadService.downloads().length">
      <div
        *ngFor="let item of downloadService.downloads(); trackBy: trackItem"
        class="dl-pill"
        [class.dl-done]="item.status === 'done'"
        [class.dl-error]="item.status === 'error'">
        @if (item.status === 'loading') {
          <span class="dl-spinner" aria-hidden="true"></span>
          <span class="dl-label">Téléchargement de <b>« {{ item.name }} »</b>…</span>
        } @else if (item.status === 'done') {
          <span class="dl-icon" aria-hidden="true">✓</span>
          <span class="dl-label"><b>« {{ item.name }} »</b> téléchargé</span>
        } @else {
          <span class="dl-icon" aria-hidden="true">✕</span>
          <span class="dl-label">Échec du téléchargement de <b>« {{ item.name }} »</b></span>
        }
      </div>
    </div>
  `,
  styles: `
    :host {
      position: fixed;
      right: 1.25rem;
      bottom: 1.25rem;
      z-index: 1100; /* au-dessus du contenu, sous les overlays PrimeNG */
      display: block;
      pointer-events: none;
    }

    .dl-stack {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      align-items: flex-end;
    }

    .dl-pill {
      display: flex;
      align-items: center;
      gap: 0.625rem;
      max-width: min(26rem, 80vw);
      padding: 0.625rem 1rem;
      border-radius: 9999px;
      background: var(--surface-card, #fff);
      border: 1px solid var(--surface-border, #e0e0e0);
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.14);
      color: var(--text-color, #333);
      font-size: 0.875rem;
      animation: dl-enter 0.18s ease-out;
    }

    .dl-label {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .dl-spinner {
      flex: none;
      width: 1.125rem;
      height: 1.125rem;
      border-radius: 50%;
      border: 2px solid var(--surface-border, #e0e0e0);
      border-top-color: var(--primary-color, #3b82f6);
      animation: dl-spin 0.7s linear infinite;
    }

    .dl-icon {
      flex: none;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1.25rem;
      height: 1.25rem;
      border-radius: 50%;
      font-size: 0.75rem;
      font-weight: 700;
      color: #fff;
    }

    .dl-done .dl-icon {
      background: #22c55e;
    }

    .dl-error .dl-icon {
      background: #ef4444;
    }

    .dl-done,
    .dl-error {
      animation: dl-enter 0.18s ease-out;
    }

    @keyframes dl-spin {
      to {
        transform: rotate(360deg);
      }
    }

    @keyframes dl-enter {
      from {
        opacity: 0;
        transform: translateY(6px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    :host-context(.app-dark) .dl-pill {
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.45);
    }
  `,
})
export class DownloadIndicatorComponent {
  downloadService = inject(DocumentDownloadService);

  trackItem(_: number, item: { id: number }) {
    return item.id;
  }
}
